import type { PaytechProviderInternalCode } from '@/queries/paymentTransaction/paymentTransaction.schema';

// `null` means: explicitly do NOT show any UI — backend only sends a block email.
// Codes not present here (unknown future codes) fall back to PAYMENT_FAILED at the call site.
export const paytechToDepositErrorCode = {
  ANTI_FRAUD_ACQUIRER_DECLINED: null,
  ANTI_FRAUD_ISSUER_DECLINED: null,
  BLACKLIST_ERROR: null,

  COMMUNICATION_PROBLEM: 'PAYMENT_FAILED',
  INTERNAL_SERVER_ERROR: 'PAYMENT_FAILED',
  // The provider times out rather than declining outright for a compliance-restricted region.
  TIMEOUT: 'REGION_NOT_SUPPORTED',
  RESTRICTED_REGION: 'REGION_NOT_SUPPORTED',
  TERMINAL_NOT_FOUND: 'PAYMENT_FAILED',
  ACQUIRER_DECLINED: 'PAYMENT_FAILED',
  REQUEST_VALIDATION_ERROR: 'PAYMENT_FAILED',
  PAYMENT_PROVIDER_ACQUIRER_MALFUNCTION: 'PAYMENT_FAILED',
  THREE_DS_INCOMPLETE: 'PAYMENT_FAILED',
  UNKNOWN_PROVIDER_ERROR_CODE: 'PAYMENT_FAILED',
  THREE_DS_DECLINED: 'PAYMENT_FAILED',
  THREE_DS_TIMEOUT: 'PAYMENT_FAILED',

  ANTI_FRAUD_INTERNAL_DECLINED: 'SECURITY_CHECK_REQUIRED',
  ANTI_FRAUD_EXTERNAL_DECLINED: 'SECURITY_CHECK_REQUIRED',

  PROCESSING_LIMITS_REACHED: 'LIMIT_REACHED',
  ACQUIRER_LIMITS_REACHED: 'LIMIT_REACHED',

  CARD_DATA_INVALID: 'INVALID_CARD',

  TRANSACTION_LIMIT_EXCEEDED: 'LIMIT_EXCEEDED',
  WITHDRAWALS_IN_CURRENCY_LIMIT_EXCEEDED: 'LIMIT_EXCEEDED',

  DUPLICATE_PAYMENT: 'PAYMENT_FAILED',

  ISSUER_DECLINED: 'BANK_DECLINED',

  INSUFFICIENT_FUNDS: 'INSUFFICIENT_FUNDS',
} as const satisfies Record<PaytechProviderInternalCode, string | null>;

export type DepositErrorCode
  = | Exclude<(typeof paytechToDepositErrorCode)[keyof typeof paytechToDepositErrorCode], null>
    | 'CARD_VERIFICATION_REQUIRED';

/**
 * Resolves a PayTech provider `internalCode` to the `deposit-result` modal code
 * to show, or `null` when no modal should open. Shared by the realtime socket
 * path and the on-return order-tracking path:
 *   - missing code            → `fallbackOnMissing` (see below)
 *   - code mapped to `null`   → null (backend emails the user instead)
 *   - unknown/unmapped code   → 'PAYMENT_FAILED' (documented fallback)
 *   - known code              → its mapped result code
 *
 * The two call sites differ ONLY on the missing-code case, so it is configurable:
 *   - socket `payment-transaction.failed`: the event itself means the deposit
 *     failed, so a missing code should still surface the generic failure modal
 *     → pass `fallbackOnMissing: 'PAYMENT_FAILED'`.
 *   - on-return order tracking: a missing code means the order has no error
 *     (e.g. it succeeded), so nothing should be shown → use the default `null`.
 */
export function resolveDepositErrorModal(
  internalCode?: string | null,
  { fallbackOnMissing = null }: { fallbackOnMissing?: DepositErrorCode | null } = {},
): DepositErrorCode | null {
  if (!internalCode) {
    return fallbackOnMissing;
  }

  const mapped = paytechToDepositErrorCode[internalCode as keyof typeof paytechToDepositErrorCode] as
    | DepositErrorCode
    | null
    | undefined;

  if (mapped === null) {
    return null;
  }

  return mapped ?? 'PAYMENT_FAILED';
}
